import React, { memo, useEffect, useMemo, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { getDates } from 'greg-cal';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import isSameDay from 'date-fns/isSameDay';
import isSameMonth from 'date-fns/isSameMonth';
import isWithinInterval from 'date-fns/isWithinInterval';
import DateCell, { EmptyDateCell } from './DateCell';
import { DAY_COLUMNS, useDate, useDateConfigWithYearMonth, useEndDate, useHolidayConfigWithYearMonth, useSetDate, useSize, useToday, } from './context';
import { BMPrimitives, useBMTheme } from '../../theme';
import useLocalization from './useLocalization';
/**
 * DateGroup component, consisted of DateCell components.
 *
 *          1   2   3   4   5
 *  6   7   8   9   10  11  12
 *  13  14  15  16  17  18  19
 *  20  21  22  23  24  25  26
 *  27  28  29  30
 */
function DateGroup(props) {
    const { activeTheme, BMComponents } = useBMTheme();
    const { colorConfig, year, month, firstDay, minDate, maxDate, todayLabel = 'Today' } = props;
    const { width } = useSize();
    const setDate = useSetDate();
    const startDate = useDate();
    const endDate = useEndDate();
    const today = useToday();
    const { dayColor } = useLocalization();
    const dateConfigYearMonth = useDateConfigWithYearMonth(year, month + 1);
    const holidayConfigYearMonth = useHolidayConfigWithYearMonth(year, month + 1);
    const [dateRows, setDateRows] = useState([]);
    const monthDates = useMemo(() => {
        let dates = getDates(year, month, firstDay);
        if (dates.length % DAY_COLUMNS !== 0) {
            dates = dates.concat(Array(DAY_COLUMNS - (dates.length % DAY_COLUMNS)).fill(''));
        }
        // replace null with '' as null won't be passed to FlatList's renderItem
        return dates.map((d) => (d === null ? '' : d));
    }, [firstDay, month, year]);
    useEffect(() => {
        const parsedMonthDates = [];
        let dateCols = [];
        monthDates.forEach((item, index) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            const key = `${year}-${month}-${index}`;
            if (item instanceof Date) {
                const date = item.getDate();
                const day = item.getDay();
                const isStartDate = isRangeAvailable && isSameDay(item, startDate);
                const isEndDate = isRangeAvailable && isSameDay(item, endDate);
                const isSelected = !isRangeAvailable && isStartDateAvailable && isSameDay(item, startDate);
                const isInRange = isRangeAvailable && !isStartDate && !isEndDate && isWithinInterval(item, { start: startDate, end: endDate });
                const isToday = isSameDay(item, today);
                const isHoliday = day % 7 === 0 || typeof holidayConfigYearMonth[date] !== 'undefined';
                const dateConfig = dateConfigYearMonth[date];
                const isDisabled = (!isSameDay(item, minDate) && isBefore(item, minDate)) || isAfter(item, maxDate) || !!(dateConfig === null || dateConfig === void 0 ? void 0 : dateConfig.disabled);
                const customLocaleColor = dayColor === null || dayColor === void 0 ? void 0 : dayColor[index % 7];
                const bottomSlot = {
                    item: (_a = dateConfig === null || dateConfig === void 0 ? void 0 : dateConfig.bottom) === null || _a === void 0 ? void 0 : _a.item,
                    type: (_b = dateConfig === null || dateConfig === void 0 ? void 0 : dateConfig.bottom) === null || _b === void 0 ? void 0 : _b.type,
                    ink: (_c = dateConfig === null || dateConfig === void 0 ? void 0 : dateConfig.bottom) === null || _c === void 0 ? void 0 : _c.ink,
                    loading: (_d = dateConfig === null || dateConfig === void 0 ? void 0 : dateConfig.bottom) === null || _d === void 0 ? void 0 : _d.loading,
                };
                const topSlot = {
                    item: (_e = dateConfig === null || dateConfig === void 0 ? void 0 : dateConfig.top) === null || _e === void 0 ? void 0 : _e.item,
                    type: (_f = dateConfig === null || dateConfig === void 0 ? void 0 : dateConfig.top) === null || _f === void 0 ? void 0 : _f.type,
                    ink: (_g = dateConfig === null || dateConfig === void 0 ? void 0 : dateConfig.top) === null || _g === void 0 ? void 0 : _g.ink,
                    loading: (_h = dateConfig === null || dateConfig === void 0 ? void 0 : dateConfig.top) === null || _h === void 0 ? void 0 : _h.loading,
                };
                dateCols.push(<DateCell key={key} date={item} onPress={setDate} isSelected={isSelected} isToday={isToday} isHoliday={isHoliday} isDisabled={isDisabled} isStartDate={isStartDate} isEndDate={isEndDate} isInRange={isInRange} bottomItem={bottomSlot.item} bottomItemType={bottomSlot.type} bottomInk={bottomSlot.ink} bottomLoading={bottomSlot.loading} topItem={topSlot.item} topItemType={topSlot.type} topInk={topSlot.ink} topLoading={topSlot.loading} todayLabel={todayLabel} selectedDateBackgroundColor={colorConfig.selectedDateBackgroundColor} selectedDateBorderColor={colorConfig.selectedDateBorderColor} selectedMidBackgroundColor={colorConfig.selectedMidBackgroundColor} customLocaleColor={customLocaleColor}/>);
            }
            else {
                let isInRange = false;
                if (isRangeAvailable) {
                    if (isSameMonth(startDate, endDate)) {
                        isInRange = false;
                    }
                    else if (isSameMonth(startDate, currentMonthDate)) {
                        isInRange = index > startDateIndex;
                    }
                    else if (isSameMonth(endDate, currentMonthDate)) {
                        isInRange = index < endDateIndex;
                    }
                    else if (isCurrentMonthWithinInterval) {
                        isInRange = true;
                    }
                }
                dateCols.push(<EmptyDateCell key={key} isInRange={isInRange} selectedMidBackgroundColor={colorConfig.selectedMidBackgroundColor}/>);
            }
            if ((index + 1) % DAY_COLUMNS === 0) {
                parsedMonthDates.push(<View key={`calRow${parsedMonthDates.length}-${year}-${month}`} style={[styles.rows]}>
            {dateCols}
          </View>);
                dateCols = [];
            }
        });
        setDateRows(parsedMonthDates);
    }, [activeTheme, startDate, endDate]);
    const isStartDateAvailable = typeof startDate !== 'undefined';
    const isEndDateAvailable = typeof endDate !== 'undefined';
    const isRangeAvailable = isStartDateAvailable && isEndDateAvailable;
    const currentMonthDate = new Date(year, month);
    const isCurrentMonthWithinInterval = isRangeAvailable
        ? isWithinInterval(currentMonthDate, { start: startDate, end: endDate })
        : false;
    const startDateIndex = isStartDateAvailable
        ? monthDates.findIndex((monthDate) => monthDate instanceof Date && isSameDay(startDate, monthDate))
        : -1;
    const endDateIndex = isEndDateAvailable
        ? monthDates.findIndex((monthDate) => monthDate instanceof Date && isSameDay(endDate, monthDate))
        : -1;
    return (<View style={[styles.container, { backgroundColor: BMComponents.BMUiCalendarBgcolorPrimary }, { width }]}>
      {dateRows}
    </View>);
}
const styles = StyleSheet.create({
    container: {
        flexDirection: 'column',
        flexWrap: 'wrap',
        justifyContent: 'center',
        paddingHorizontal: BMPrimitives.BMSize.sm,
        paddingVertical: BMPrimitives.BMSize.xs,
    },
    rows: {
        flexDirection: 'row',
    },
});
export default memo(DateGroup);
